import { createRoute } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import {
  useDiscoverableClubsQuery,
  useSetDiscoverableMutation,
  type DiscoverableClubRow,
} from "@/lib/staff/discoverable-clubs";
import styles from "./staff-discoverable-clubs.module.scss";

function StaffDiscoverableClubs() {
  const query = useDiscoverableClubsQuery();
  const setDiscoverable = useSetDiscoverableMutation();

  const rows = query.data ?? [];
  const onCount = rows.filter((r) => r.discoverable).length;

  return (
    <main className={styles.shell}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Discoverable clubs</h1>
          <p className={styles.sub}>
            {query.data
              ? `${onCount} of ${rows.length} local clubs are surfaced on /discover`
              : "Loading clubs"}
          </p>
        </div>
      </header>

      {query.isError ? (
        <div className={styles.errorBanner} role="alert">
          Failed to load clubs:{" "}
          {(query.error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.tHead}>
            <tr>
              <th>Club</th>
              <th>Owner</th>
              <th>Members</th>
              <th style={{ textAlign: "right" }}>Discoverable</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <ClubRow
                key={row.id}
                row={row}
                pending={
                  setDiscoverable.isPending &&
                  setDiscoverable.variables?.clubId === row.id
                }
                onToggle={(next) =>
                  setDiscoverable.mutate({ clubId: row.id, discoverable: next })
                }
              />
            ))}
            {!query.isLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.empty}>
                  No local clubs yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function ClubRow({
  row,
  pending,
  onToggle,
}: {
  row: DiscoverableClubRow;
  pending: boolean;
  onToggle: (next: boolean) => void;
}) {
  const checkboxId = `discoverable-${row.id}`;
  return (
    <tr className={styles.row}>
      <td>
        <div className={styles.clubCell}>
          <span className={styles.clubName}>{row.name}</span>
          <span className={styles.clubMeta}>
            {row.city ?? "No city"}
            {row.isLocalClub ? "" : " · not local"}
          </span>
        </div>
      </td>
      <td>
        <div className={styles.clubCell}>
          <span>{row.ownerName ?? "—"}</span>
          {row.ownerEmail ? (
            <span className={styles.clubMeta}>{row.ownerEmail}</span>
          ) : null}
        </div>
      </td>
      <td>{row.memberCount}</td>
      <td style={{ textAlign: "right" }}>
        <label htmlFor={checkboxId} className={styles.toggle}>
          <input
            id={checkboxId}
            type="checkbox"
            className={styles.toggleInput}
            checked={row.discoverable}
            disabled={pending}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <span
            className={`${styles.toggleLabel} ${
              row.discoverable ? styles.toggleLabelOn : ""
            }`}
          >
            {row.discoverable ? "On" : "Off"}
          </span>
        </label>
      </td>
    </tr>
  );
}

export const staffDiscoverableClubsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/discoverable-clubs",
  component: StaffDiscoverableClubs,
});
